import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthProvider';
import { organizationSwitcherMemberships } from '../../../auth/organization-access';
import { roleLabel, statusLabel } from '../../users/types/user';

function initials(value: string | null | undefined): string {
  const source = (value ?? 'Route42').trim();
  const local = source.split('@')[0];
  const parts = local.split(/[._\-\s]+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : local.slice(0, 2)).toUpperCase() || 'R42';
}

function membershipStatusClass(status: string, customerStatus: string | null): string {
  if (customerStatus === 'SUSPENDED') return 'r42-profile-status--suspended';
  return status === 'ACTIVE'
    ? 'r42-profile-status--active'
    : status === 'INVITED'
      ? 'r42-profile-status--invited'
      : status === 'SUSPENDED'
        ? 'r42-profile-status--suspended'
        : 'r42-profile-status--removed';
}

function errorText(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Route42 could not update your password. Please try again.';
}

export function ProfilePage() {
  const auth = useAuth();
  const profile = auth.profile;
  const email = profile?.user.email ?? null;
  const accessibleMemberships = useMemo(
    () => profile?.memberships.filter((membership) => membership.access_available) ?? [],
    [profile],
  );
  const organizationChoices = useMemo(
    () => organizationSwitcherMemberships(profile?.memberships ?? []),
    [profile],
  );

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  async function submitPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPasswordNotice(null);
    setPasswordError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Complete all password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('Choose a new password that is different from your current password.');
      return;
    }

    setPasswordBusy(true);
    try {
      await auth.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordNotice('Password updated successfully.');
    } catch (error) {
      setPasswordError(errorText(error));
    } finally {
      setPasswordBusy(false);
    }
  }

  if (!profile) return null;

  return (
    <section className="r42-page r42-page--wide r42-profile-page">
      <div className="r42-page-header r42-profile-header">
        <div>
          <span className="r42-eyebrow">Account settings</span>
          <h1>Profile</h1>
          <p>Review your Route42 identity, organization access, and account security.</p>
        </div>
      </div>

      <div className="r42-profile-identity-card">
        <div className="r42-profile-avatar" aria-hidden="true">{initials(email ?? profile.user.username)}</div>
        <div className="r42-profile-identity-copy">
          <span className="r42-eyebrow">Route42 account</span>
          <h2>{email ?? 'Signed-in Route42 user'}</h2>
          <div className="r42-profile-identity-meta">
            <span className={profile.user.email_verified ? 'is-verified' : 'is-unverified'}>
              <i className={`bi ${profile.user.email_verified ? 'bi-patch-check-fill' : 'bi-exclamation-circle'}`} aria-hidden="true" />
              {profile.user.email_verified ? 'Email verified' : 'Email not verified'}
            </span>
            <span>{accessibleMemberships.length} active organization{accessibleMemberships.length === 1 ? '' : 's'}</span>
          </div>
        </div>
      </div>

      <div className="r42-profile-grid">
        <div className="r42-profile-column">
          <section className="r42-profile-card" aria-labelledby="profile-account-heading">
            <div className="r42-profile-card-head">
              <div>
                <span className="r42-eyebrow">Identity</span>
                <h2 id="profile-account-heading">Account information</h2>
              </div>
            </div>
            <dl className="r42-profile-details">
              <div>
                <dt>Email address</dt>
                <dd>
                  <strong>{email ?? 'Not available'}</strong>
                  {profile.user.email_verified ? <span className="r42-profile-inline-status"><i className="bi bi-check-circle" aria-hidden="true" /> Verified</span> : null}
                </dd>
              </div>
              <div>
                <dt>Route42 user ID</dt>
                <dd><code>{profile.user.user_id}</code></dd>
              </div>
            </dl>
            <p className="r42-profile-card-note">
              Your sign-in email is managed as part of your Route42 identity. Email changes are not self-service in this release.
            </p>
          </section>

          <section className="r42-profile-card" aria-labelledby="profile-organizations-heading">
            <div className="r42-profile-card-head r42-profile-card-head--action">
              <div>
                <span className="r42-eyebrow">Access</span>
                <h2 id="profile-organizations-heading">Organizations & roles</h2>
              </div>
              {organizationChoices.length > 1 ? <Link className="btn btn-outline-secondary btn-sm" to="/organizations">Switch organization</Link> : null}
            </div>
            <div className="r42-profile-memberships" aria-label="Your Route42 organizations">
              {profile.memberships.map((membership) => {
                const current = membership.customer_id === auth.activeCustomerId && membership.access_available;
                return (
                  <article className={`r42-profile-membership${current ? ' is-current' : ''}`} key={membership.membership_id}>
                    <div className="r42-profile-membership-main">
                      <div>
                        <strong>{membership.customer_name ?? membership.customer_id}</strong>
                        <span>{membership.facility_ids.length ? `${membership.facility_ids.length} facility assignment${membership.facility_ids.length === 1 ? '' : 's'}` : 'All facilities'}</span>
                      </div>
                      <div className="r42-profile-membership-state">
                        {current ? <span className="r42-profile-current">Current</span> : null}
                        <span className={`r42-profile-status ${membershipStatusClass(membership.membership_status, membership.customer_status)}`}>
                          {membership.customer_status === 'SUSPENDED' ? 'Organization suspended' : statusLabel(membership.membership_status)}
                        </span>
                      </div>
                    </div>
                    <div className="r42-profile-role-list">
                      {membership.role_ids.length
                        ? membership.role_ids.map((role) => <span key={role}>{roleLabel(role)}</span>)
                        : <span>Route42 member</span>}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>

        <div className="r42-profile-column">
          <section className="r42-profile-card" aria-labelledby="profile-security-heading">
            <div className="r42-profile-card-head">
              <div>
                <span className="r42-eyebrow">Security</span>
                <h2 id="profile-security-heading">Change password</h2>
                <p>Update the password used to sign in to Route42.</p>
              </div>
            </div>

            {passwordNotice ? <div className="r42-profile-notice" role="status"><i className="bi bi-check-circle" aria-hidden="true" /> {passwordNotice}</div> : null}
            {passwordError ? <div className="r42-profile-error" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /> {passwordError}</div> : null}

            <form className="r42-profile-password-form" onSubmit={(event) => void submitPassword(event)}>
              <label className="r42-field">
                <span>Current password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  disabled={passwordBusy}
                  required
                />
              </label>
              <label className="r42-field">
                <span>New password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={passwordBusy}
                  required
                />
              </label>
              <label className="r42-field">
                <span>Confirm new password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={passwordBusy}
                  required
                />
              </label>
              <p className="r42-profile-password-note">
                Your password is updated directly with the Route42 identity provider and must satisfy the configured password policy.
              </p>
              <div className="r42-profile-password-actions">
                <button className="btn r42-btn-primary" type="submit" disabled={passwordBusy || !currentPassword || !newPassword || !confirmPassword}>
                  {passwordBusy ? <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Updating…</> : 'Update password'}
                </button>
              </div>
            </form>
          </section>

          <section className="r42-profile-card r42-profile-card--quiet" aria-labelledby="profile-session-heading">
            <div className="r42-profile-card-head">
              <div>
                <span className="r42-eyebrow">Current context</span>
                <h2 id="profile-session-heading">Route42 workspace</h2>
              </div>
            </div>
            <div className="r42-profile-context-row">
              <i className="bi bi-building" aria-hidden="true" />
              <div>
                <strong>{auth.activeMembership?.customer_name ?? 'No organization selected'}</strong>
                <span>{auth.activeMembership ? auth.activeMembership.role_ids.map(roleLabel).join(' · ') || 'Route42 member' : 'Select an organization to work with machines.'}</span>
              </div>
            </div>
            {organizationChoices.length > 1 ? <Link className="r42-profile-context-link" to="/organizations">Switch organization <i className="bi bi-arrow-right" aria-hidden="true" /></Link> : null}
          </section>
        </div>
      </div>
    </section>
  );
}
